// src/context/AdminDashboardContext.tsx
import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { AdminDashboardDataInterface } from '../shared/type/administrator.type';
import { useApi } from './ApiProvider';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { useParams } from 'react-router-dom';

interface AdminDashboardContextProps {
  dashboardData?: AdminDashboardDataInterface;
  loading: boolean;
  fetchDashboardData: () => Promise<void>;
}

const AdminDashboardContext = createContext<AdminDashboardContextProps | undefined>(undefined);

export const useAdminDashboardContext = () => {
  const context = useContext(AdminDashboardContext);
  if (!context) {
    throw new Error('useAdminDashboardContext must be used within an AdminDashboardProvider');
  }
  return context;
};

export const AdminDashboardProvider = ({ children }: { children: React.ReactNode }) => {
  const [dashboardData, setDashboardData] = useState<AdminDashboardDataInterface | undefined>();
  const [loading, setLoading] = useState(false);
  const { id: tenantId } = useParams<{ id: string }>();
  const { apiAdminDashboardController } = useApi();

  const fetchDashboardData = useCallback(async () => {
    if (!tenantId || tenantId === 'undefined') return;
    setLoading(true);
    try {
      const response: AxiosResponse<AdminDashboardDataInterface> =
        await apiAdminDashboardController('').get('', {
          params: { tenantId }
        });

      if (response.status === HttpStatusCode.Ok) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchDashboardData();
    }
  }, [tenantId, fetchDashboardData]);

  const value = {
    dashboardData,
    loading,
    fetchDashboardData
  };

  return <AdminDashboardContext.Provider value={value}>{children}</AdminDashboardContext.Provider>;
};
